import {Modal, Skeleton} from '@mui/material'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import i18n from '../../../../i18n'
import {CrossIcon} from '../../../../assets/icons/cross'
import {ScrollWrapper} from '../sharedStyles'
import {DotIcon} from '../../../../assets/icons/dot'
import {Status} from './HealthStatus'
import {BiomarkerLines, getSignalColorsByLetter, TO_FIXED_DEF} from './MeasuredBiomarkers'
import {TextLinkify} from "./RecomendAndNextSteps";
import { fixUploadUrlsInHtml } from '../utils/appHelpers';

const Background = styled.div<{ bgColor: string }>`
  background-color: #fff;
  padding: 18px 24px;
  overflow: hidden;

  ${({bgColor}) =>
    bgColor &&
    `background-color: linear-gradient(180deg, ${bgColor}, #fff, #fff)`};
  
  @media print {
    background-color: #f5f5f5!important;
  }

  > div {
    padding-right: 20px;
  }

  @media (max-width: 1100px) {
    max-width: 400px;
    margin: 20px;
  }
`

const Title = styled.p`
  color: var(--text-main, #1a1a1a);
  font-family: Roboto;
  font-size: 24px;
  font-weight: 500;
  line-height: 36px;
  letter-spacing: 0.48px;
  margin: 0px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  svg { cursor: pointer; }
`

const Box = styled.div`
  margin-bottom: 15px;
  span {
    color: var(--text-main, #1a1a1a);
    font-family: Roboto;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.32px;
    margin-bottom: 10px;
  }
  p {
    color: #1a1a1a;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 300;
    line-height: 24px;
    letter-spacing: 0.28px;
  }
`
const SignalLevel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SignalLevelDescription = styled.div`
  display: flex;
  align-items: center;
  p {
    margin: 10px 0px;
    color: var(--text-main, #1a1a1a);
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 300;
    line-height: 20px;
    letter-spacing: 0.28px;
    display: flex;
    align-items: center;
  }
  span {
    margin: 5px 0px;
    color: var(--text-main, #1a1a1a);
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.14px;
  }
`
const SignalLevelText = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    width: 15px;
    height: 15px;
  }
`

const Total = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 20px;
  div {
    margin-right: 10px;
    p {
      margin: 0;
      color: var(--text-main, #1a1a1a);
      font-size: 32px;
      font-weight: 700;
      line-height: 32px;
      letter-spacing: 0.64px;
    }
    div {
      display: flex;
      align-items: center;
      p {
        margin-right: 5px;
        color: var(--text-secondary, #808080);
        text-align: center;
        font-family: Roboto;
        font-size: 14px;
        font-weight: 300;
        line-height: 20px;
        letter-spacing: 0.28px;
      }
      span {
        color: var(--text-secondary, #808080);
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: 0.14px;
      }
    }
  }
`
export const _decodeURI = (name: any) => {
    try {
        name = decodeURI(name)
    } catch(e) {}
    return name;
}
const SignalLevelComponent = ({indicator}: { indicator: any }) => {
    const { t } = useTranslation()
    indicator ??= {}

    let type = _decodeURI(indicator.unitType.name || '');
    let unitType = indicator.unitTypes.find((it: any) => it.name == type)
    let {toFixed} = unitType || {}
    toFixed = +toFixed || TO_FIXED_DEF
    let pub = (v: any) => {
        try {
            return +(v || 0).toFixed(toFixed) || '0'
        } catch(e) {
            return +v;
        }
    }

    return (
      <Box data-animch={3}>
      {indicator?._values.map((it: any, ind: number) => {
          let color = getSignalColorsByLetter(it.scaleValue, it._scaleValue)
          const Inf = <Infinity top={1}/>;
          const isLast = ind == indicator?._values.length - 1;
          return (
              <SignalLevel key={ind} className={'signalLevelWrap'}>
                  <SignalLevelText>
                      <DotIcon color={color.color}/>
                      {color.name}
                  </SignalLevelText>
                  <SignalLevelDescription>
                      <p>
                          {ind == 1000 ? <span style={{
                              marginRight: '5px',
                              display: 'inline-block'
                          }}>{Inf}</span> : pub(it.from)} &mdash; {isLast ?
                          <span style={{marginLeft: '5px'}}>{Inf}</span> : (pub(it.to) || '-')}
                      </p>
                  </SignalLevelDescription>
              </SignalLevel>
          )
      })}
      </Box>
    )
}


export const Infinity = (props: any) => {
    return <div
        style={{display: 'inline-block', width: '13px', position: 'relative', height: '15px', padding: "0px 5px 0 0"}}>
        <svg width="15" height="15" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"
             style={{marginTop: (props.top || 0) + 'px', position: 'absolute', left: '0'}}
        >
            <mask id="mask0_195_21658" style={{"maskType": "alpha"}} maskUnits="userSpaceOnUse" x="0" y="4" width="18"
                  height="10">
                <path
                    d="M7.49535 10.6943L6.88363 11.3834C6.41238 11.8538 5.81225 12.174 5.15909 12.3035C4.50593 12.433 3.82905 12.3659 3.21397 12.1108C2.5989 11.8558 2.07324 11.4241 1.70343 10.8703C1.33361 10.3166 1.13623 9.66568 1.13623 8.99981C1.13623 8.33394 1.33361 7.68301 1.70343 7.12928C2.07324 6.57554 2.5989 6.14386 3.21397 5.88877C3.82905 5.63369 4.50593 5.56665 5.15909 5.69612C5.81225 5.8256 6.41238 6.14578 6.88363 6.61621L11.1164 11.3834C11.5877 11.8538 12.1878 12.174 12.841 12.3035C13.4941 12.433 14.171 12.3659 14.7861 12.1108C15.4012 11.8558 15.9268 11.4241 16.2966 10.8703C16.6665 10.3166 16.8638 9.66568 16.8638 8.99981C16.8638 8.33394 16.6665 7.68301 16.2966 7.12928C15.9268 6.57554 15.4012 6.14386 14.7861 5.88877C14.171 5.63369 13.4941 5.56665 12.841 5.69612C12.1878 5.8256 11.5877 6.14578 11.1164 6.61621L10.5047 7.30528"
                    stroke={props.color || "black"} stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            </mask>
            <g mask="url(#mask0_195_21658)">
                <path d="M18 0H0V18H18V0Z" fill={props.color || "#1A1A1A"}/>
            </g>
        </svg>
    </div>
}

const AboutBiomarker = ({
    onClose,
    indicator,
    value,
}: {
    indicator?: any
    onClose: () => void
    value?: number
}) => {
    const { i18n } = useTranslation();
    
    document.onkeydown = (e) => {
        const {keyCode: code} = e
        if (+code == 27) {
            document.onkeydown = null
            onClose()
        }
    }

    let colors: any = {
        R: '#E96863',
        G: '#C2EAED',
        A: '#FFECB3',
    }

    const getLocalizedString = (value: any): string => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const currentLang = i18n.language || 'en';
            const langCode = currentLang.split('-')[0];
            return value[currentLang] || value[langCode] || value.en || value.de || (Object.values(value)[0] as string) || '';
        }
        return String(value || '');
    };

    const getDescriptionText = (): string => {
        const description = indicator.description;
        if (!description) return '';
        
        if (typeof description === 'object' && description !== null && !Array.isArray(description)) {
            const currentLang = i18n.language || 'en';
            const langCode = currentLang.split('-')[0];
            
            let text: string = '';
            
            const enKey = 'en';
            
            if (description[langCode] && typeof description[langCode] === 'string') {
                text = description[langCode];
            } else if (description[currentLang] && typeof description[currentLang] === 'string') {
                text = description[currentLang];
            } else if (description[enKey] && typeof description[enKey] === 'string') {
                text = description[enKey];
            } else {
                const firstKey = Object.keys(description)[0];
                if (firstKey && typeof description[firstKey] === 'string') {
                    text = description[firstKey];
                }
            }
            
            return text || '';
        }
        
        return typeof description === 'string' ? description : String(description || '');
    };

    const indicatorTitle = getLocalizedString(indicator.name);

    return (
      <div data-biomarker>
        <ScrollWrapper data-animch>
            <CrossIcon
                data-cross
                onClick={onClose}
                style={{cursor: 'pointer', position: 'absolute', right: '0px'}}
            />
            <div className="paddModal">
                <Title>{indicatorTitle}</Title>
                <Total data-animch>
                    <div>
                        <p>{value}
                            <div className={'badgeWrap'}><BadgeLabel {...indicator}></BadgeLabel></div>
                        </p>
                    </div>
                </Total>
                <div style={{margin: '30px 0px 50px 0'}}>
                    <BiomarkerLines indicator={indicator} isBig={true}></BiomarkerLines>
                </div>
                <SignalLevelComponent indicator={indicator}/>
                <div data-animch={10}>
                    <TextLinkify text={getDescriptionText()} data-animch></TextLinkify>
                </div>
            </div>
        </ScrollWrapper>
    </div>
    )
}

export const getSignalColorsByLetterLocalized = (t: any) => ({
    red: { background: '#000', color: 'darkred', name: t('attention') },
    DARK_RED: { background: '#FCECEA', borderColor: '#daaca6', color: '#c52423', name: t('renalFailure') },
    R2: { background: '#FCECEA', color: '#F29B95', name: t('severeFailure') },
    A2: { background: '#fffaee', color: '#ffca1c', name: t('slightlyReduced') },
    LIGHT_RED: { background: 'rgb(252,242,234)', color: 'rgb(242,196,149)', textColor: 'rgb(216,149,61)', name: t('moderateInsufficiency') },
    R: { background: '#FCECEA', borderColor: '#F8D1CC', color: 'rgba(216,63,61,0.71)', name: t('attention') },
    GREY: { background: '#efefef', color: '#999', name: t('unknown') },
    G: { background: '#E8F4F1', borderColor: 'rgba(33,139,143,0.33)', color: 'rgba(33,139,143,0.63)', name: t('optimal') },
    GL: { background: '#EDF9F3', borderColor: 'rgba(123,220,181,0.4)', color: '#4FAD88', name: t('optimal') },
    A: { background: '#fffaee', color: '#ffca1c', borderColor: 'rgba(255,202,28,0.4)', textColor: '#e5b51a', name: t('monitor') },
})
export const UnitType = (props: any) => {
    let indicator = props?.indicator || props
    let label = props?.label || ''
    let name = _decodeURI(indicator?.unitType?.name || '--');
    return <>
        {label && <span className={'labelProps'}>{label}</span>}
        {name}</>
}
export const Description = (props: any) => {
    let {value} = props || {}
    return (<div data-animch={10}>
            <div dangerouslySetInnerHTML={{__html: fixUploadUrlsInHtml(value || '---')}}></div>
        </div>
    )
}

export const BadgeLabel = (indicator: any) => {
    const { t } = useTranslation()
    let level = indicator?.scaleValue || 'GREY'
    let el = getSignalColorsByLetter(level, indicator?._scaleValue)

    return (
        <div
            className={'badgeItem'}
            style={{
                display: 'inline-block',
                color: (el as any).textColor || el.color,
                background: el.background,
                padding: '0px 12px',
                borderRadius: '15px',
                fontWeight: 'bold',
                border: '1px solid ' + ((el as any).borderColor || el.color),
            }}
        >
            {el.name}
        </div>
    )
}

export const signalColorsByLetter: any = {
  red: { background: '#000', color: 'darkred', get name() { return i18n.t('attention') } },
  DARK_RED: { background: '#FCECEA', borderColor: '#daaca6', color: '#c52423', get name() { return i18n.t('renalFailure') } },
  R2: { background: '#FCECEA', color: '#F29B95', get name() { return i18n.t('severeFailure') } },
  A2: { background: '#fffaee', color: '#ffca1c', get name() { return i18n.t('slightlyReduced') } },
  LIGHT_RED: { background: 'rgb(252,242,234)', color: 'rgb(242,196,149)', textColor: 'rgb(216,149,61)', get name() { return i18n.t('moderateInsufficiency') } },
  R: { background: '#FCECEA', borderColor: '#F8D1CC', color: 'rgba(216,63,61,0.71)', get name() { return i18n.t('attention') } },
  GREY: { background: '#efefef', color: '#999', get name() { return i18n.t('unknown') } },
  G: { background: '#E8F4F1', borderColor: 'rgba(33,139,143,0.33)', color: 'rgba(33,139,143,0.63)', get name() { return i18n.t('optimal') } },
  GL: { background: '#EDF9F3', borderColor: 'rgba(123,220,181,0.4)', color: '#4FAD88', get name() { return i18n.t('suboptimal') } },
  A: { background: '#fffaee', color: '#ffca1c', borderColor: 'rgba(255,202,28,0.4)', textColor: '#e5b51a', get name() { return i18n.t('moderateRisk') } },
}

export default AboutBiomarker
